// src/components/InfoTooltip/InfoTooltip.js
import React from 'react';
import { Tooltip } from 'react-tooltip'; // Import the Tooltip component
import './info-tooltip.css'; // Import component-specific styles

/**
 * A reusable component to display an info icon with a tooltip.
 *
 * Props:
 * - text: The text content to display inside the tooltip. Required.
 * - idSuffix: A unique suffix to ensure tooltip IDs are unique if multiple
 * tooltips are on the same page. Defaults to a random string
 * but providing a specific one related to the context is better.
 * - place: Where the tooltip should appear relative to the icon
 * (e.g., 'top', 'bottom', 'left', 'right'). Defaults to 'top'.
 * - effect: Visual effect ('float' or 'solid'). Defaults to 'solid'.
 */
function InfoTooltip({ text, idSuffix, place = 'top', effect = 'solid' }) {
  // Generate a reasonably unique ID for the tooltip instance
  // Using idSuffix makes it more predictable and stable than Math.random()
  const tooltipId = `info-tooltip-${idSuffix || Math.random().toString(36).substring(7)}`;

  // Basic validation
  if (!text) {
    // Don't render anything if there's no text to show
    // Optionally log a warning during development
    if (process.env.NODE_ENV !== 'production') {
        console.warn('[InfoTooltip] Missing required "text" prop.');
    }
    return null;
  }

  return (
    <>
      {/* The trigger element: a styled span with the icon */}
      {/* data-tooltip-id connects this element to the Tooltip component */}
      {/* data-tooltip-content sets the text for this specific trigger */}
      <span
        className="info-tooltip-trigger"
        data-tooltip-id={tooltipId}
        data-tooltip-content={text}
        data-tooltip-place={place}
        data-tooltip-effect={effect}
        aria-label="More information" // Accessibility
      >
        ? {/* The visible icon */}
      </span>

      {/* The Tooltip component itself (renders the actual popup) */}
      {/* It needs to be rendered once per unique ID */}
      {/* We add common styling via className */}
      <Tooltip
        id={tooltipId}
        className="info-tooltip-popup" // Class for styling the tooltip box
        // Other options like delayShow, delayHide, etc., can be added here
      />
    </>
  );
}

export default InfoTooltip;